import React, { useState } from 'react';
import styled from 'styled-components';
import {
  type ApolloClient,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  InputAdornment,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { FileDownload, Delete } from '@mui/icons-material';
import { useFormik, FormikProvider } from 'formik';
import { boolean, object, string } from 'yup';
import { GSReturnOptions } from './options';
import { GSAssessmentGenFile } from './GSAssessmentGenFile';
import { GS_DECISION } from './constant';
import { PAGE_TEXT } from '../../../lang';
import {
  type GsDecision,
  type SupersedeData,
  useGenerateOfferFiles,
  useGetPreviewData,
  useRemoveOfferFiles,
  useUpdateAssignee,
} from '../../../graphql';
import {
  Button,
  TextField,
  Radio,
  type SelectOption,
  type AlertDetails,
  Alert,
  type FileData,
  Dialog,
  Select,
  IconButton,
} from '../../../../../components';
import { useAuth } from '../../../../../contexts';

type GsAssessmentBoxProps = {
  initialValues: GsDecision;
  previewData: SupersedeData;
  gsAssigneeOption: SelectOption[];
  remarksFromDsrc?: string;
  isViewMode: boolean;
  needPanel?: boolean;
  refetchGsDecision: () => void;
  onSubmit: (values: GsDecision) => void;
};

const STEP_TEXT = PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT;
const { DECISION } = STEP_TEXT;

const commonSvgIconStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  '& .MuiSvgIcon-root': {
    marginTop: '-0.25rem',
    fontSize: 18,
  },
};

export const GSAssessmentBox: React.FunctionComponent<
  GsAssessmentBoxProps
> = props => {
  const {
    initialValues,
    previewData: initialPreviewData,
    onSubmit,
    needPanel = false,
    remarksFromDsrc = '',
    isViewMode,
    gsAssigneeOption,
    refetchGsDecision,
  } = props;

  const { applicationId = '' } = useParams();
  const {
    fileChecks,
    firstCheckedAt,
    firstCheckedBy,
    secondCheckedAt,
    secondCheckedBy,
    assignee,
  } = initialValues ?? {};
  const [previewData, setPreviewData] =
    useState<SupersedeData>(initialPreviewData);

  const { user } = useAuth();
  const { sub } = user ?? {};

  const isFirstChecked: boolean = firstCheckedAt !== '';
  const isFirstCheckedNotByMe: boolean =
    isFirstChecked && firstCheckedBy !== sub;
  const isSecondChecked: boolean = secondCheckedAt !== '';

  const showSubmitBtn: boolean =
    !isViewMode &&
    ((!isFirstChecked && !isSecondChecked) ||
      (isFirstChecked && isFirstCheckedNotByMe && !isSecondChecked));

  // the first checker is not allowed to do second check
  const filteredGsAssigneeOption = gsAssigneeOption.filter(
    assignee => assignee.value !== firstCheckedBy,
  );

  const checkingInfo = {
    isFirstChecked,
    isSecondChecked,
    firstCheckedAt,
    firstCheckedBy,
    secondCheckedAt,
    secondCheckedBy,
  };

  // if funding letter has been generated, display the <GSAssessmentGenFile />
  const [genLoading, setGenLoading] = useState<boolean>(false);
  const [genedFiles, setGenedFiles] = useState<FileData | null>(
    fileChecks?.[0]?.file ?? null,
  );

  const [openAlert, setOpenAlert] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const updateAssignee = useUpdateAssignee();
  const handleUpdateAssignee = async (
    e: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    try {
      const variables = {
        input: {
          applicationId,
          assignee: e.target.value as string,
        },
      };
      await updateAssignee({ variables });
      setAlertDetails({
        alertContent: 'Assignee Updated successfully',
        alertType: 'success',
      });
      refetchGsDecision();
    } catch (error) {
      setAlertDetails({
        alertContent: 'Failed to assign result',
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const decisionOption: SelectOption[] = [
    { value: GS_DECISION.RETURN, label: DECISION.RETURN },
    { value: GS_DECISION.REJECT, label: DECISION.REJECT },
    ...(needPanel
      ? [
          {
            value: GS_DECISION.TRANSFER_TO_OTHER_PANEL,
            label: DECISION.TRANSPER,
          },
        ]
      : [{ value: GS_DECISION.OFFER_PROVISIONED, label: DECISION.OFFER }]),
  ];

  const formik = useFormik<GsDecision>({
    initialValues,
    validationSchema: object({
      decision: string().required(),
      gsDecision: string().when(['decision'], {
        is: (decision: string) => {
          return decision === GS_DECISION.RETURN;
        },
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      remarks: string().when(['decision'], {
        is: (decision: string) => {
          return decision === GS_DECISION.RETURN;
        },
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      fundingLetterChecked: boolean().nullable(),
    }),
    onSubmit: (values: GsDecision) => {
      setSubmitDialogOpen(true);
    },
  });
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const generateFile = useGenerateOfferFiles();
  const handleGenerateFile = async (): Promise<void> => {
    setGenLoading(true);
    try {
      const variables = {
        applicationId,
      };
      const generateFileRes = await generateFile({
        variables,
      });

      const fundingLeter = generateFileRes?.data?.generateOfferFiles?.[0];
      if (fundingLeter as boolean) {
        const { file } = fundingLeter;
        setGenedFiles(file as FileData);
        const previewData = await useGetPreviewData(client, applicationId);

        setPreviewData(previewData);

        setGenLoading(false);
        await formik.setValues({
          ...formik.values,
          fileChecks: [
            {
              file: file as FileData,
              checked: false,
            },
          ],
        });
      }
    } catch (error) {
      setGenLoading(false);
      setAlertDetails({
        alertContent: `Error Occurs.${(error as Error)?.message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const isToOffer: boolean =
    formik.values?.decision === GS_DECISION.OFFER_PROVISIONED; // select the `Make offer to the Applicant`

  const isToReturn: boolean = formik.values?.decision === GS_DECISION.RETURN; // select the `Return`

  const cancelSubmitGsDecision = (): void => {
    setSubmitDialogOpen(false);
  };

  const confirmSubmitGsDecision = (): void => {
    setSubmitDialogOpen(false);
    onSubmit(formik.values);
  };

  const removeOfferFiles = useRemoveOfferFiles();

  const handleRemoveOfferFiles = async (): Promise<void> => {
    try {
      const variables = {
        applicationId,
      };

      await removeOfferFiles({
        variables,
      });
      refetchGsDecision();
      setAlertDetails({
        alertContent: 'Removed the File Successfully.',
        alertType: 'success',
      });
      setOpenAlert(true);
      setGenedFiles(null);
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Removed the File Failed.${(error as Error).message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleSubmitButtonClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    await formik.submitForm();
    await formik.setTouched({
      gsDecision: true,
      remarks: true,
    });
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const errorElement = document.getElementsByName(
            Object.keys(errors)[0],
          )[0];
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      });

      return;
    }

    setSubmitDialogOpen(true);
  };

  const handleGSDecisionChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const value = event.target.value as GS_DECISION;
    await formik.setFieldValue('decision', value);
    await formik.setFieldValue('gsDecision', '');
    await formik.setFieldValue('remarks', '');
  };
  const generateFileBtnDisable =
    isViewMode || genLoading || (isFirstChecked && !isFirstCheckedNotByMe);
  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={submitDialogOpen}
        dialogTitle={
          formik.values.decision !== GS_DECISION.ACCEPT
            ? STEP_TEXT.NO_CHECK_SUBMIT_CONFIRM.TITLE
            : isFirstChecked
              ? STEP_TEXT.SECOND_CHECK_SUBMIT_CONFIRM.TITLE
              : STEP_TEXT.FIRST_CHECK_SUBMIT_CONFIRM.TITLE
        }
        dialogAction={PAGE_TEXT.SUBMIT}
        cancelAction={PAGE_TEXT.CANCEL}
        onDialogAction={confirmSubmitGsDecision}
        onDialogClose={cancelSubmitGsDecision}
        width="600px"
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {formik.values.decision !== GS_DECISION.ACCEPT
            ? STEP_TEXT.NO_CHECK_SUBMIT_CONFIRM.CONTENT
            : isFirstChecked
              ? STEP_TEXT.SECOND_CHECK_SUBMIT_CONFIRM.CONTENT
              : STEP_TEXT.FIRST_CHECK_SUBMIT_CONFIRM.CONTENT}
        </Typography>
      </Dialog>
      <ContentBox>
        <Box className="w-full bg-[#ECECEC]" padding={4}>
          <div className="font-bold mb-4">{STEP_TEXT.REMARKS.DRC}</div>
          <TextField
            className="w-full"
            multiline
            minRows={3}
            disabled={true}
            value={remarksFromDsrc}
          />
        </Box>
        <p>&nbsp;</p>
        {/* TODO depends on if need panel */}
        {/* <Box className="w-full bg-[#ECECEC]" padding={4}>
          <div className="font-bold mb-4 ">{STEP_TEXT.REMARKS.PANEL}</div>
          <TextField className="w-full" multiline rows={2} disabled={true} />
        </Box> */}
        <Box className="w-full" paddingX={4} paddingTop={4}>
          <div className="font-bold mb-8">{STEP_TEXT.GS_DECISION}</div>
          <FormControl className="!pl-8">
            <RadioGroup
              name="decision"
              value={formik.values.decision}
              onChange={handleGSDecisionChange}
            >
              {decisionOption.map((item: SelectOption, index: number) => {
                const { value: gsvalue, label } = item;
                return (
                  <div key={index} className="mb-4 flex flex-row items-start">
                    <Radio
                      value={gsvalue as GS_DECISION}
                      disabled={
                        isViewMode || (isFirstChecked && !isFirstCheckedNotByMe)
                      }
                      onChange={formik.handleChange}
                      checked={formik.values?.decision === gsvalue}
                      error={
                        Boolean(formik.errors.decision) ??
                        Boolean(formik.touched.decision)
                      }
                      label={
                        <span
                          style={{
                            fontWeight:
                              formik.values?.decision === gsvalue
                                ? 'bold'
                                : 'normal',
                          }}
                        >
                          {label}
                        </span>
                      }
                      sx={{ ...commonSvgIconStyle }}
                    />
                    {!index && isToReturn && (
                      <Select
                        name="gsDecision"
                        disabled={
                          isViewMode ||
                          (isFirstChecked && !isFirstCheckedNotByMe)
                        }
                        options={GSReturnOptions}
                        value={formik.values.gsDecision}
                        InputLabelProps={{ shrink: true }}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          Boolean(formik.errors.gsDecision) &&
                          formik.touched.gsDecision
                        }
                        size="small"
                        helperText={
                          formik.touched.gsDecision && formik.errors.gsDecision
                        }
                        className="w-[10rem]"
                      />
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          </FormControl>
          {isToOffer && (
            <div className="w-full flex justify-end">
              <Tooltip title={'Remove the generated NOO'}>
                <span>
                  <Button
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: '700' }}
                    disabled={genedFiles === null || generateFileBtnDisable}
                    onClick={handleRemoveOfferFiles}
                    startIcon={
                      <InputAdornment position="end">
                        <IconButton
                          icon={<Delete />}
                          disabled={
                            genedFiles === null || generateFileBtnDisable
                          }
                          color="primary"
                          title="Generate Funding Letter"
                        />
                      </InputAdornment>
                    }
                  >
                    {STEP_TEXT.REMOVE}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={`${
                  genedFiles !== null
                    ? STEP_TEXT.CLICK_TO_REGEN
                    : STEP_TEXT.CLICK_TO_GEN
                } the NOO`}
              >
                <Button
                  variant="text"
                  sx={{ textTransform: 'none', fontWeight: '700' }}
                  disabled={generateFileBtnDisable}
                  onClick={handleGenerateFile}
                  startIcon={
                    <InputAdornment position="end">
                      <IconButton
                        icon={<FileDownload />}
                        color="primary"
                        disabled={generateFileBtnDisable}
                        title="Generate Funding Letter"
                      />
                    </InputAdornment>
                  }
                >
                  {genedFiles !== null
                    ? STEP_TEXT.CLICK_TO_REGEN
                    : STEP_TEXT.CLICK_TO_GEN}
                </Button>
              </Tooltip>
            </div>
          )}
        </Box>
        <div className="flex flex-col items-center w-full">
          {isToOffer && (genLoading || genedFiles !== null) && (
            <div className="w-full">
              {genLoading ? (
                <div className="flex flex-col items-center mt-10 mb-16">
                  <div className="half-circle-spinner text-center">
                    <div className="circle circle-1" />
                    <div className="circle circle-2" />
                  </div>
                </div>
              ) : (
                <FormikProvider value={formik}>
                  <form onSubmit={formik.handleSubmit} noValidate>
                    <GSAssessmentGenFile
                      applicationId={applicationId}
                      previewData={previewData}
                      genedFiles={genedFiles}
                      checkingInfo={checkingInfo}
                    />
                  </form>
                </FormikProvider>
              )}
            </div>
          )}

          <div className="pl-8 py-6 w-full">
            <div className="font-bold my-6">
              {STEP_TEXT.REMARKS.RETURN_REMARKS}&nbsp;
              {isToReturn ? STEP_TEXT.REMARKS.REMARKS_TO_AU : '(if any)'}
            </div>
            <TextField
              className="w-full !px-8"
              multiline
              required={isToReturn}
              rows={3}
              name="remarks"
              InputLabelProps={{ shrink: true }}
              value={formik.values.remarks}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={
                isViewMode || (isFirstChecked && !isFirstCheckedNotByMe)
              }
              error={Boolean(formik.errors.remarks) && formik.touched.remarks}
              helperText={formik.touched.remarks && formik.errors.remarks}
            />
          </div>
          <div className={`text-center my-8 `}>
            <div className={`${isToOffer ? '' : 'hidden'}`}>
              <h2>{STEP_TEXT.GEN_OFFER_NOTE}</h2>
              <h2 className="mb-4">{STEP_TEXT.DOUBLE_CHECK}</h2>
            </div>
            {showSubmitBtn && (
              <Button
                variant="contained"
                onClick={handleSubmitButtonClick}
                disabled={
                  (isToOffer && genedFiles === null) || genLoading || isViewMode
                }
              >
                {PAGE_TEXT.SUBMIT}
              </Button>
            )}
          </div>
          {isFirstChecked && !isSecondChecked && (
            <div className="w-full flex flex-col p-8">
              <div className="justify-start flex py-4 font-semibold">
                {STEP_TEXT.NEXT_ASSIGNEE}
              </div>
              <div className="justify-start flex pl-8">
                <Select
                  sx={{
                    width: '20rem',
                  }}
                  options={filteredGsAssigneeOption}
                  disabled={isViewMode}
                  size="small"
                  value={assignee ?? null}
                  onChange={async e => {
                    await handleUpdateAssignee(e);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </ContentBox>
    </form>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  margin: 0.5rem 1rem 0;

  h2 {
    font-style: italic;
    color: #951d1d;
  }

  .flex-container {
    display: flex;
    align-items: center;
  }

  .half-circle-spinner,
  .half-circle-spinner * {
    box-sizing: border-box;
  }

  .half-circle-spinner {
    width: 60px;
    height: 60px;
    border-radius: 100%;
    position: relative;
  }

  .half-circle-spinner .circle {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 100%;
    border: calc(60px / 10) solid transparent;
  }

  .half-circle-spinner .circle.circle-1 {
    border-top-color: #951d1d;
    animation: half-circle-spinner-animation 1s infinite;
  }

  .half-circle-spinner .circle.circle-2 {
    border-bottom-color: #951d1d;
    animation: half-circle-spinner-animation 1s infinite alternate;
  }

  @keyframes half-circle-spinner-animation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
